// Полоса вкладок экрана из серверного tab_bar: до 4 — поровну по ширине, больше — горизонтальный скролл; подпись всегда
// целиком (не влезла поровну — скролл и при 4).
// whole (экран матча, Figma 839:32196): вкладки по ширине подписи, растягиваются до края, полоса прокручивается всегда.
// tabWidth (Figma Tab 794:36282 / 794:36283): вкладки не уже tabWidth px, поровну растягиваются до края, не влезли —
// полоса листается; подпись целиком одной строкой: длиннее — поля сжимаются до 8, затем вкладка шире (HQ-1908 v2).
import { useEffect, useRef, type CSSProperties } from 'react';
import { haptic } from '../telegram/webapp';
import styles from './ServerTabStrip.module.css';

/** Отступ подписи прошлого дня от левого края полосы (Figma 794:36570: «Yesterday» на x = 20). */
export const EDGE = 20;

/** Рамки вкладки в координатах содержимого полосы; labelLeft — где начинается текст подписи. */
export interface TabBox { left: number; width: number; labelLeft: number }

/** Куда прокрутить полосу, чтобы показать активную вкладку: `center` — в середину; `previous` — подпись предыдущей
 *  вкладки целиком на EDGE от левого края (Figma «Матч-центр» 794:36570: «Yesterday · Today · Tomorrow · Su…»),
 *  справа обрезана следующая подпись. Считается от замеренных рамок, а не от ширины вкладки константой: подпись
 *  центрирована во вкладке, и её начало зависит от метрик шрифта (HQ-1065 v2: «Yesterday» уезжал за левый край). */
export function stripScrollLeft(align: 'center' | 'previous', tabs: TabBox[], index: number, clientWidth: number): number {
  const active = tabs[index];
  if (!active) return 0;
  if (align === 'center') return active.left - (clientWidth - active.width) / 2;
  return index > 0 ? Math.max(0, tabs[index - 1]!.labelLeft - EDGE) : 0;
}

/** Рамки вкладок полосы по offsetLeft/offsetWidth: полоса — offsetParent вкладок, вкладка — своей подписи (CSS). */
export function measureTabs(strip: HTMLElement): TabBox[] {
  return ([...strip.children] as HTMLElement[]).map((b) => {
    const label = b.firstElementChild as HTMLElement | null;
    return { left: b.offsetLeft, width: b.offsetWidth, labelLeft: b.offsetLeft + (label?.offsetLeft ?? 0) };
  });
}

/** whole — полоса прокручивается всегда (вкладки своей ширины), а не делится поровну при ≤ 4 вкладках.
 *  tabWidth — ширина вкладки Figma в px при тексте 17 (не уже её, длинная подпись — шире без «…»; крупный шрифт — шире в той же
 *  доле); без него — прежняя раскладка. */
export function ServerTabStrip({ tabs, active, onSelect, whole = false, align = 'center', tabWidth }: {
  tabs: Array<{ key: string; label: string }>; active: string; onSelect: (key: string) => void;
  whole?: boolean; align?: 'center' | 'previous'; tabWidth?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // активную вкладку — на место, прокручивая ТОЛЬКО саму полосу: scrollIntoView заодно двигал страницу
  // по вертикали (команда и турнир открывались прокрученными вниз)
  useEffect(() => {
    const strip = ref.current;
    if (!strip) return;
    const index = [...strip.children].findIndex((b) => b.getAttribute('aria-selected') === 'true');
    if (index < 0) return;
    const place = () => stripScrollLeft(align, measureTabs(strip), index, strip.clientWidth);
    strip.scrollLeft = place();
    let placed = strip.scrollLeft; // браузер мог урезать до предела прокрутки
    // веб-шрифт мог доехать после первого замера — переставить, если человек ещё не прокручивал полосу сам
    let alive = true;
    void document.fonts?.ready.then(() => {
      if (!alive || Math.abs(strip.scrollLeft - placed) > 1) return;
      strip.scrollLeft = place();
      placed = strip.scrollLeft;
    });
    return () => { alive = false; };
  }, [active, align]);
  if (tabs.length < 2) return null;
  return (
    <div ref={ref} className={`${styles.strip} ${tabWidth || whole || tabs.length > 4 ? styles.scroll : ''}${tabWidth ? ` ${styles.fixed}` : ''}`}
      role="tablist" style={tabWidth ? { '--tab-w': tabWidth } as CSSProperties : undefined}>
      {tabs.map((tab) => (
        <button key={tab.key} role="tab" aria-selected={tab.key === active}
          className={`${styles.tab} ${tab.key === active ? styles.active : ''}`}
          onClick={() => { if (tab.key !== active) { haptic.selection(); onSelect(tab.key); } }}>
          <span className={styles.label}>{tab.label}</span>
        </button>
      ))}
    </div>
  );
}
